// A question from the assistant, with answers to pick. One pick sends at once; several, or a typed answer, send with the button
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { Check, MessageCircleQuestion } from "lucide-react"
import type { QuestionAnswer, QuestionRequest } from "@/lib/assistant/types"

export function QuestionCard({ question, onAnswer }: {
  question: QuestionRequest
  onAnswer: (answer: QuestionAnswer | null) => void
}) {
  const [picked, setPicked] = useState<string[]>([])
  const [other, setOther] = useState("")
  const { options, multiple, allowOther } = question

  const choose = (option: string) => {
    if (!multiple) { onAnswer({ choices: [option] }); return }
    setPicked(prev => (prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]))
  }
  const typed = other.trim()
  const canSend = picked.length > 0 || !!typed
  const send = () => { if (canSend) onAnswer({ choices: picked, ...(typed ? { other: typed } : {}) }) }

  return (
    <div className="space-y-2.5 border-t bg-primary/5 p-3">
      <div className="flex items-start gap-2">
        <MessageCircleQuestion className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p className="text-sm font-medium">{question.question}</p>
      </div>

      <div className="flex flex-col gap-1.5">
        {options.map(option => {
          const on = picked.includes(option)
          return (
            <button
              key={option}
              type="button"
              onClick={() => choose(option)}
              className={cn(
                "flex items-center gap-2 rounded-lg border bg-background px-3 py-2 text-left text-sm transition-colors hover:border-primary/50 hover:bg-primary/5",
                on && "border-primary bg-primary/10",
              )}
            >
              {multiple && (
                <span className={cn("flex h-4 w-4 shrink-0 items-center justify-center rounded border", on && "border-primary bg-primary text-primary-foreground")}>
                  {on && <Check className="h-3 w-3" />}
                </span>
              )}
              <span className="min-w-0 flex-1">{option}</span>
            </button>
          )
        })}
      </div>

      {allowOther && (
        <input
          value={other}
          onChange={e => setOther(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); send() } }}
          placeholder="Something else…"
          className="h-8 w-full rounded-lg border bg-background px-3 text-sm outline-none focus:border-primary/50"
        />
      )}

      <div className="flex items-center gap-2">
        {(multiple || allowOther) && (
          <Button size="sm" className="h-7 text-xs" disabled={!canSend} onClick={send}>Send answer</Button>
        )}
        <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={() => onAnswer(null)}>Skip</Button>
        <span className="ml-auto text-[10px] text-muted-foreground">or type an answer below</span>
      </div>
    </div>
  )
}
